import { FeedbackService, QuestionsService } from "@/client"
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button, Stack, Tabs } from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { FeedbackSubmitForm } from "./FeedbackSubmitForm"
import { FeedbackThread } from "./FeedbackThread"
import { QuestionDisplay } from "./QuestionDisplay"

interface FeedbackModalProps {
  open: boolean
  onClose: () => void
  quizId: string
  questionId: string
  isTeacher: boolean
}

export function FeedbackModal({
  open,
  onClose,
  quizId,
  questionId,
  isTeacher,
}: FeedbackModalProps) {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<"question" | "view" | "submit">(
    "view",
  )

  // Fetch question data
  const { data: question, isLoading: isLoadingQuestion } = useQuery({
    queryKey: ["question", questionId],
    queryFn: () => QuestionsService.getQuestion({ questionId }),
    enabled: open,
  })

  // Fetch feedback
  const { data: feedbackList = [], isLoading } = useQuery({
    queryKey: ["feedback", quizId, questionId],
    queryFn: () =>
      FeedbackService.getQuestionFeedback({
        quizId,
        questionId,
      }),
    enabled: open,
  })

  // Submit feedback mutation
  const submitMutation = useMutation({
    mutationFn: (feedbackText: string) =>
      FeedbackService.submitFeedback({
        quizId,
        questionId,
        requestBody: { feedback_text: feedbackText },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["feedback", quizId, questionId],
      })
      setActiveTab("view")
    },
  })

  // Reply mutation
  const replyMutation = useMutation({
    mutationFn: ({
      feedbackId,
      feedbackText,
      isPublished,
    }: {
      feedbackId: string
      feedbackText: string
      isPublished: boolean
    }) =>
      FeedbackService.replyToFeedback({
        feedbackId,
        requestBody: {
          feedback_text: feedbackText,
          is_published: isPublished,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["feedback", quizId, questionId],
      })
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({
      feedbackId,
      feedbackText,
    }: {
      feedbackId: string
      feedbackText: string
    }) =>
      FeedbackService.updateFeedback({
        feedbackId,
        requestBody: { feedback_text: feedbackText },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["feedback", quizId, questionId],
      })
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (feedbackId: string) =>
      FeedbackService.deleteFeedback({ feedbackId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["feedback", quizId, questionId],
      })
    },
  })

  // Resolve mutation
  const resolveMutation = useMutation({
    mutationFn: (feedbackId: string) =>
      FeedbackService.resolveFeedback({ feedbackId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["feedback", quizId, questionId],
      })
    },
  })

  const hasOwnFeedback = Array.isArray(feedbackList)
    ? feedbackList.some((f) => f.is_own && !f.parent_id)
    : false

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => !e.open && onClose()}
      size="xl"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Question Feedback</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>

        <DialogBody>
          <Tabs.Root
            value={activeTab}
            onValueChange={(e) =>
              setActiveTab(e.value as "question" | "view" | "submit")
            }
          >
            <Tabs.List>
              <Tabs.Trigger value="question">Question</Tabs.Trigger>
              <Tabs.Trigger value="view">
                Feedback (
                {Array.isArray(feedbackList) ? feedbackList.length : 0})
              </Tabs.Trigger>
              {!isTeacher && !hasOwnFeedback && (
                <Tabs.Trigger value="submit">Submit Feedback</Tabs.Trigger>
              )}
            </Tabs.List>

            <Tabs.Content value="question" pt={4}>
              {isLoadingQuestion ? (
                <p>Loading question...</p>
              ) : question ? (
                <QuestionDisplay question={question} />
              ) : (
                <p>Question not found</p>
              )}
            </Tabs.Content>

            <Tabs.Content value="view" pt={4}>
              {isLoading ? (
                <p>Loading feedback...</p>
              ) : !Array.isArray(feedbackList) || feedbackList.length === 0 ? (
                <p>No feedback yet. Be the first to share your thoughts!</p>
              ) : (
                <Stack gap={4}>
                  {feedbackList
                    .filter((f) => !f.parent_id) // Only show root feedback
                    .map((feedback) => (
                      <FeedbackThread
                        key={feedback.id}
                        feedback={feedback}
                        isTeacher={isTeacher}
                        onReply={
                          isTeacher
                            ? async (feedbackText, isPublished) => {
                                await replyMutation.mutateAsync({
                                  feedbackId: feedback.id,
                                  feedbackText,
                                  isPublished,
                                })
                              }
                            : undefined
                        }
                        onEdit={
                          feedback.is_own && feedback.status !== "resolved"
                            ? async (feedbackText) => {
                                await updateMutation.mutateAsync({
                                  feedbackId: feedback.id,
                                  feedbackText,
                                })
                              }
                            : undefined
                        }
                        onDelete={
                          feedback.is_own && feedback.status !== "resolved"
                            ? async () => {
                                await deleteMutation.mutateAsync(feedback.id)
                              }
                            : undefined
                        }
                        onResolve={
                          isTeacher && feedback.status !== "resolved"
                            ? async () => {
                                await resolveMutation.mutateAsync(feedback.id)
                              }
                            : undefined
                        }
                      />
                    ))}
                </Stack>
              )}
            </Tabs.Content>

            {!isTeacher && !hasOwnFeedback && (
              <Tabs.Content value="submit" pt={4}>
                <FeedbackSubmitForm
                  onSubmit={async (feedbackText) => {
                    await submitMutation.mutateAsync(feedbackText)
                  }}
                  isSubmitting={submitMutation.isPending}
                />
              </Tabs.Content>
            )}
          </Tabs.Root>
        </DialogBody>

        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline">Close</Button>
          </DialogActionTrigger>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
